import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputField, Toggle, Divider, Button } from '@ets/design-system';

import { useDispatch, useSelector } from '_common/hooks';

import { editDescription } from 'App/redux/appSlice';

import { Textarea, type TextareaProps } from '_common/lib/components';

import styles from './Description.module.scss';

type DescriptionProps = {
  canEdit?: boolean;
};

const Description = ({ canEdit }: DescriptionProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const elementId = Object.keys(useSelector((state) => state.table.selected))[0];
  const object = useSelector((state) => state.app.data[elementId]);

  const [edit, setEdit] = useState(false);
  const [currentDescription, setCurrentDescription] = useState(object?.description);

  useEffect(() => {
    setCurrentDescription(object?.description);
    setEdit(false);
  }, [elementId]);

  const handleEnableEdit = () => {
    setEdit(true);
  };

  const handleDisabledEdit = () => {
    setEdit(false);
  };

  const handleSaveDescription = () => {
    dispatch(
      editDescription({
        objectId: elementId,
        objectType: object.type,
        newDescription: currentDescription,
      }),
    );

    setEdit(false);
  };

  const handleSetDescription: TextareaProps['onChange'] = (e) => {
    const target = e.target as HTMLTextAreaElement;
    setCurrentDescription(target.value);
  };

  const renderDescription = () => {
    /**Edit and has permissions*/
    if (edit) {
      return (
        <>
          <InputField
            size="m"
            label={intl.formatMessage({ id: 'global.description' })}
            testId="information-panel-description"
          >
            <Textarea
              plainText
              name="description"
              type="text"
              size="m"
              style={{ height: '9rem' }}
              initialContent={currentDescription}
              onChange={handleSetDescription}
              placeholder={intl.formatMessage({ id: 'INSERT_YOUR_TEXT' })}
              testId="information-panel-description-textarea"
            />
          </InputField>
          <div className={styles.actions}>
            <Button
              size="s"
              variant="link"
              onClick={handleDisabledEdit}
              testId="information-panel-description-close"
            >
              <FormattedMessage id="global.cancel" />
            </Button>
            <Button
              size="s"
              variant="primary"
              onClick={handleSaveDescription}
              testId="information-panel-description-save"
            >
              <FormattedMessage id="global.save" />
            </Button>
          </div>
        </>
      );
    } else {
      return (
        <div className={styles.editableDescription}>
          {canEdit ? (
            <>
              <div
                className={styles.descriptionLabel}
                data-testid="information-panel-description-label"
              >
                {object.description || intl.formatMessage({ id: 'ADD_DESCRIPTION' })}
              </div>
              <Toggle
                variant="link"
                size="m"
                iconProps={{ icon: 'pen-to-square' }}
                onClick={handleEnableEdit}
                testId="information-panel-edit-description"
              />
            </>
          ) : (
            <div
              className={`${styles.descriptionLabel} ${styles.noPermissions}`}
              data-testid="information-panel-description-label"
            >
              {object.description || intl.formatMessage({ id: 'NO_DESCRIPTION' })}
            </div>
          )}
        </div>
      );
    }
  };

  return (
    <div className={styles.root}>
      <Divider margin="0 1rem" size="calc(100% - 2rem)" />
      <div className={styles.descriptionWrapper}>{renderDescription()}</div>
    </div>
  );
};

export default Description;
